import React, { useMemo } from 'react';
import {
  GASTOS_REPORT_TITULO_DEFAULT,
  applyGastosColumnLabels,
  buildTipoGastosLookup,
  buildGastosTipoSummaryRows,
  formatGastosCell,
  groupGastosByTipo,
  mergeGastosEncabezado,
  resolveGastosGrandTotal,
  resolveGastosTipoGastoTexto,
  rowToGastosNorm,
  sumGastosImporteTotal,
  withGastosPeriodoLine,
} from '../../../utils/gastosReportModel';
import { encabezadoTieneDatos } from '../../../utils/reporteEncabezadoApi';
import ReporteFiltrosEncabezadoLista from '../ReporteFiltrosEncabezadoLista/ReporteFiltrosEncabezadoLista';
import '../PopularidadReportLayout/PopularidadReportLayout.css';
import '../DescuentosCortesiasReportLayout/DescuentosCortesiasReportLayout.css';
import '../CancelacionesReportLayout/CancelacionesReportLayout.css';
import './GastosReportLayout.css';

const meaningfulHeaderLine = (v) => v != null && String(v).trim() !== '';

export default function GastosReportLayout({
  className = '',
  rawRows = [],
  fechaInicio,
  fechaFin,
  cajaInicio,
  cajaFin,
  turnoInicio,
  turnoFin,
  encabezadoApi = null,
  encabezadoFallback = null,
  reporteTitulo = GASTOS_REPORT_TITULO_DEFAULT,
  apiTotales = null,
  columnasLabels = null,
  sucursalNombre = '',
  tiposCatalogo = [],
  tipoGastoFiltro = '',
  globalView = false,
}) {
  const form = useMemo(
    () => ({ fechaInicio, fechaFin, cajaInicio, cajaFin, turnoInicio, turnoFin }),
    [fechaInicio, fechaFin, cajaInicio, cajaFin, turnoInicio, turnoFin]
  );

  const tipoLookup = useMemo(
    () => buildTipoGastosLookup(tiposCatalogo),
    [tiposCatalogo],
  );

  const enc = useMemo(() => {
    const m = mergeGastosEncabezado(encabezadoApi, encabezadoFallback);
    const merged = withGastosPeriodoLine(
      { ...m, reporteTitulo: m.reporteTitulo || reporteTitulo },
      fechaInicio,
      fechaFin,
      form
    );
    const tipoGastoTexto = resolveGastosTipoGastoTexto(
      tipoGastoFiltro,
      merged.tipoGastoTexto,
      tiposCatalogo,
    );
    return tipoGastoTexto ? { ...merged, tipoGastoTexto } : merged;
  }, [
    encabezadoApi,
    encabezadoFallback,
    reporteTitulo,
    fechaInicio,
    fechaFin,
    form,
    tipoGastoFiltro,
    tiposCatalogo,
  ]);

  const resolved = useMemo(
    () => applyGastosColumnLabels(columnasLabels, globalView),
    [columnasLabels, globalView],
  );

  const { groups, grandTotal, summaryRows } = useMemo(() => {
    const normRows = rawRows.map((r) => rowToGastosNorm(r, tipoLookup));
    const grouped = groupGastosByTipo(normRows);
    return {
      groups: grouped,
      grandTotal: resolveGastosGrandTotal(apiTotales, sumGastosImporteTotal(normRows)),
      summaryRows: globalView ? buildGastosTipoSummaryRows(grouped) : [],
    };
  }, [rawRows, apiTotales, tipoLookup, globalView]);

  const hasRows = globalView
    ? summaryRows.length > 0
    : groups.some((g) => g.rows.length > 0);

  const tituloMostrar = String(enc.reporteTitulo || reporteTitulo).trim().toUpperCase();

  return (
    <div className={`dc-report canc-report pop-report gastos-report ${className}`.trim()}>
      <header className="pop-report-headerenc gastos-report-header">
        {encabezadoTieneDatos(enc) &&
        enc.bloqueTexto &&
        !enc.nombreComercial &&
        !enc.reporteTitulo ? (
          <pre className="pop-report-headerenc__bloque">{enc.bloqueTexto}</pre>
        ) : (
          <>
            {(meaningfulHeaderLine(enc.nombreComercial) ||
              meaningfulHeaderLine(enc.rfc)) && (
              <div className="pop-report-headerenc__empresa">
                {meaningfulHeaderLine(enc.nombreComercial) && (
                  <h1 className="pop-report-headerenc__nombre">
                    {String(enc.nombreComercial).trim().toUpperCase()}
                    {sucursalNombre &&
                      !String(enc.nombreComercial)
                        .toUpperCase()
                        .includes(String(sucursalNombre).toUpperCase()) && (
                        <span className="pop-report-headerenc__sucursal-append">
                          {' '}
                          - {String(sucursalNombre).trim().toUpperCase()}
                        </span>
                      )}
                  </h1>
                )}
                {meaningfulHeaderLine(enc.domicilioFiscal) && (
                  <p className="pop-report-headerenc__detalle pop-report-headerenc__domicilio">
                    {String(enc.domicilioFiscal).trim()}
                  </p>
                )}
                {meaningfulHeaderLine(enc.rfc) && (
                  <p className="pop-report-headerenc__detalle pop-report-headerenc__rfc">
                    RFC: {String(enc.rfc).trim().toUpperCase()}
                  </p>
                )}
              </div>
            )}
            <div className="pop-report-headerenc__titulos">
              <h2 className="pop-report-headerenc__reporte dc-report-title-highlight">
                {tituloMostrar}
              </h2>
            </div>
            <div className="pop-report-headerenc__filtros">
              {meaningfulHeaderLine(enc.periodoTexto) && (
                <p className="pop-report-headerenc__filtros-line">{enc.periodoTexto}</p>
              )}
              <ReporteFiltrosEncabezadoLista
                className="reporte-filtros-meta--report"
                label="Sucursales"
                texto={enc.sucursalesTexto || sucursalNombre}
                items={enc.sucursalesItems}
              />
              <ReporteFiltrosEncabezadoLista
                className="reporte-filtros-meta--report"
                label="Cajas"
                texto={
                  enc.cajasTexto ||
                  (cajaInicio != null || cajaFin != null
                    ? `${cajaInicio ?? '—'} a ${cajaFin ?? '—'}`
                    : '')
                }
                items={enc.cajasItems}
              />
              <p className="pop-report-headerenc__filtros-line">
                {enc.turnosTexto ? (
                  <>Turnos: {String(enc.turnosTexto)}</>
                ) : (
                  <>
                    Turnos: <strong>{turnoInicio ?? '—'}</strong> a{' '}
                    <strong>{turnoFin ?? '—'}</strong>
                  </>
                )}
              </p>
              {meaningfulHeaderLine(enc.tipoGastoTexto) && (
                <p className="pop-report-headerenc__filtros-line">
                  Tipo de gasto: {String(enc.tipoGastoTexto)}
                </p>
              )}
            </div>
          </>
        )}
      </header>

      <div className="dc-report-table-wrap">
        <table className="dc-report-table gastos-report-table canc-report-table">
          <thead>
            <tr>
              {resolved.map((c) => (
                <th key={c.id} className={c.align === 'right' ? 'dc-num' : 'dc-text'}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!hasRows ? (
              <tr>
                <td className="dc-report-empty-row" colSpan={resolved.length}>
                  Sin gastos en el rango seleccionado.
                </td>
              </tr>
            ) : globalView ? (
              summaryRows.map((r, ri) => (
                <tr key={`summary-${ri}`}>
                  {resolved.map((c) => (
                    <td
                      key={`${ri}-${c.id}`}
                      className={c.align === 'right' ? 'dc-num' : 'dc-text'}
                    >
                      {formatGastosCell(c, r[c.id])}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              groups.map((g, gi) => (
                <React.Fragment key={`g-${gi}-${g.tipo}`}>
                  <tr className="gastos-report-group-header">
                    <td colSpan={resolved.length}>{g.label}</td>
                  </tr>
                  {g.rows.map((r, ri) => (
                    <tr key={`${gi}-${ri}`}>
                      {resolved.map((c) => (
                        <td
                          key={`${gi}-${ri}-${c.id}`}
                          className={c.align === 'right' ? 'dc-num' : 'dc-text'}
                        >
                          {formatGastosCell(c, r[c.id])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="gastos-report-group-subtotal">
                    <td colSpan={resolved.length - 1} />
                    <td className="dc-num">
                      {formatGastosCell({ kind: 'money' }, g.subtotal)}
                    </td>
                  </tr>
                </React.Fragment>
              ))
            )}
          </tbody>
          {hasRows && (
            <tfoot>
              <tr className="gastos-report-grand-total">
                <td className="dc-text">Total de gastos</td>
                {globalView ? (
                  <td className="dc-num">{formatGastosCell({ kind: 'money' }, grandTotal)}</td>
                ) : (
                  <>
                    <td colSpan={resolved.length - 2} />
                    <td className="dc-num">{formatGastosCell({ kind: 'money' }, grandTotal)}</td>
                  </>
                )}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
